import type { CodeDef } from '@/shared/helpers/dataHelpers';
import { isConditionTypeAllowed, type ColumnFilterModel, type FilterModel, type SetFilterModel } from './CodeGridFilters';
import { getColumnFilter } from './CodeGrid.helper';

/**
 * Encodes the grid's filter into a readable URL parameter, so that a shared codeset link can be
 * understood, and sanity checked, without decoding it. For example:
 *
 *     ?filter=isValid=True;description=Pump seal,Pump casing
 *     ?filter=dateCreated=2024-01-01..2024-01-31;weight>5
 *
 * The syntax deliberately avoids characters that have to be percent encoded, so the parameter
 * stays legible in the address bar.
 *
 * | Form | Meaning |
 * | --- | --- |
 * | `field=a` | equals, or a set filter with one value |
 * | `field=a,b` | a set filter with several values |
 * | `field!=a` | not equal, or the values a set filter excludes |
 * | `field~a` | contains |
 * | `field>a` `field>=a` `field<a` `field<=a` | comparisons |
 * | `field=a..b` | a range, or a span of days |
 * | `field=null` | blank |
 * | `field!=null` | not blank |
 * | `field=` | nothing selected, which matches nothing |
 *
 * In the excluding form the blanks stay selected unless `null` is listed among the exclusions, so
 * `ProjectMaster!=Alpha` keeps the rows without a project and `ProjectMaster!=Alpha,null` does not.
 *
 * The excluding form means exactly what it reads: *everything except these values*. A value added
 * to the code set after the link was made is therefore selected when the link is opened. That
 * matches how the same filter is sent to the API, which compares with `!=` rather than listing the
 * values, so the grid and the export agree.
 *
 * A value is wrapped in single quotes when it would otherwise be ambiguous, and an inner quote is
 * doubled: `description='Pump, seal'` and `description='It''s'`.
 *
 * Which of the several meanings of `=` applies is decided by the filter the column actually uses,
 * so a link whose filter no longer suits the column is dropped rather than misapplied.
 */

/**
 * Anything longer than this makes a link awkward to share, and some proxies and mail clients
 * truncate it. A filter that does not fit is simply left out of the URL.
 */
export const maxFilterParamLength = 1500;

const NULL_TOKEN = 'null';
const CONDITION_SEPARATOR = ';';
const VALUE_SEPARATOR = ',';
const RANGE_SEPARATOR = '..';

type FilterKind = 'set' | 'text' | 'number' | 'date';

export function encodeFilterModel(model: FilterModel | null, distinctValues: Map<string, string[]>): string | null {
    if (!model || Object.keys(model).length === 0)
        return null;

    const conditions: string[] = [];

    for (const [field, columnFilter] of Object.entries(model)) {
        const condition = columnFilter.filterType === 'set'
            ? encodeSetFilter(field, columnFilter, distinctValues)
            : encodeSimpleFilter(field, columnFilter);

        if (condition)
            conditions.push(condition);
    }

    if (conditions.length === 0)
        return null;

    const encoded = conditions.join(CONDITION_SEPARATOR);

    return encodeURIComponent(encoded).length > maxFilterParamLength ? null : encoded;
}

export function decodeFilterModel(encoded: string | null, columns: CodeDef[], distinctValues: Map<string, string[]>): FilterModel | null {
    if (!encoded)
        return null;

    const model: FilterModel = {};

    for (const condition of splitTop(encoded, CONDITION_SEPARATOR)) {
        if (!condition.trim())
            continue;

        try {
            const parsed = parseCondition(condition);
            if (!parsed)
                continue;

            const column = columns.find(c => c.field === parsed.field);
            if (!column)
                continue;

            const columnFilter = buildFilter(parsed, getFilterKind(column), distinctValues);
            if (columnFilter)
                model[parsed.field] = columnFilter;
        } catch {
            // A condition that cannot be understood is ignored rather than left to break the grid.
        }
    }

    return Object.keys(model).length > 0 ? model : null;
}

export function getFilterKind(column: CodeDef): FilterKind {
    switch (getColumnFilter(column)) {
        case 'agSetColumnFilter': return 'set';
        case 'agNumberColumnFilter': return 'number';
        case 'agDateColumnFilter': return 'date';
        default: return 'text';
    }
}

export type GridUrlState = { filter?: string; group?: string; sort?: string };

/**
 * Whether a link carries grid state of its own. A link describes the whole view, so the dimensions
 * it leaves out are cleared rather than left at whatever this browser had saved. A link with no
 * grid parameters at all carries nothing, and the saved state is restored instead.
 */
export function hasGridUrlState(urlState?: GridUrlState): boolean {
    return !!(urlState?.filter || urlState?.group || urlState?.sort);
}

// ---------------------------------------------------------------------------- encoding

function encodeSetFilter(field: string, filter: SetFilterModel, distinctValues: Map<string, string[]>): string {
    const values = filter.values ?? [];
    const blanksSelected = values.some(value => value === null || value === '');
    const selected = values.filter((value): value is string => value !== null && value !== '');

    if (selected.length === 0)
        return blanksSelected ? `${field}=${NULL_TOKEN}` : `${field}=`;

    // A set filter with most values selected is written as the values it excludes, so that
    // deselecting a few of several hundred references does not fill the URL. This only holds when
    // every selected value is known, otherwise the recipient cannot rebuild the same selection.
    // Blanks are included unless they are listed among the exclusions.
    const known = distinctValues.get(field);
    if (known && known.length > 0) {
        const knownSet = new Set(known);

        if (selected.every(value => knownSet.has(value))) {
            const selectedSet = new Set(selected);
            const excluded = known.filter(value => !selectedSet.has(value));
            const listedExclusions = blanksSelected ? excluded.map(quote) : [...excluded.map(quote), NULL_TOKEN];

            // With nothing excluded and the blanks left out this reads `field!=null`, which keeps
            // "everything that has a value" short however many values the column has. Listing them
            // all could push the parameter over the limit, which drops the whole filter, not just
            // this column. Nothing excluded with the blanks selected is every row, and would read
            // as an empty `field!=`, so that case falls through to the listing form below.
            if (listedExclusions.length > 0 && listedExclusions.length < selected.length)
                return `${field}!=${listedExclusions.join(VALUE_SEPARATOR)}`;
        }
    }

    const listed = blanksSelected ? [NULL_TOKEN, ...selected.map(quote)] : selected.map(quote);

    return `${field}=${listed.join(VALUE_SEPARATOR)}`;
}

function encodeSimpleFilter(field: string, filter: ColumnFilterModel): string | null {
    if (filter.filterType === 'set')
        return null;

    const type = filter.type;
    if (type === 'blank')
        return `${field}=${NULL_TOKEN}`;
    if (type === 'notBlank')
        return `${field}!=${NULL_TOKEN}`;

    if (type === 'inRange') {
        const from = filter.filterType === 'date' ? filter.dateFrom : filter.filter;
        // Only the number filter offers a second bound; text has no `filterTo` to read.
        let to: string | number | null | undefined;
        if (filter.filterType === 'date')
            to = filter.dateTo;
        else if (filter.filterType === 'number')
            to = filter.filterTo;

        if (from === null || from === undefined || to === null || to === undefined)
            return null;

        return `${field}=${quote(toText(from))}${RANGE_SEPARATOR}${quote(toText(to))}`;
    }

    const value = filter.filterType === 'date' ? filter.dateFrom : filter.filter;
    if (value === null || value === undefined || value === '')
        return null;

    const operator = encodeOperator(type);
    if (!operator)
        return null;

    return `${field}${operator}${quote(toText(value))}`;
}

function encodeOperator(type: string): string | null {
    switch (type) {
        case 'equals': return '=';
        case 'notEqual': return '!=';
        case 'contains': return '~';
        case 'greaterThan': return '>';
        case 'greaterThanOrEqual': return '>=';
        case 'lessThan': return '<';
        case 'lessThanOrEqual': return '<=';
        default: return null;
    }
}

// A date is stored with a time, which is not part of what the user chose.
function toText(value: string | number): string {
    return typeof value === 'number' ? String(value) : value.split(' ')[0];
}

function quote(value: string): string {
    const needsQuotes = value === ''
        || value === NULL_TOKEN
        || value !== value.trim()
        || /[',;]/.test(value)
        || value.includes(RANGE_SEPARATOR);

    return needsQuotes ? `'${value.replace(/'/g, '\'\'')}'` : value;
}

// ---------------------------------------------------------------------------- decoding

type ParsedCondition = {
    field: string;
    operator: string;
    operand: string;
};

const operators = ['!=', '>=', '<=', '=', '~', '>', '<'];

function parseCondition(condition: string): ParsedCondition | null {
    const match = /^\s*([A-Za-z_][A-Za-z0-9_]*)\s*/.exec(condition);
    if (!match)
        return null;

    const field = match[1];
    const rest = condition.slice(match[0].length);
    const operator = operators.find(candidate => rest.startsWith(candidate));
    if (!operator)
        return null;

    return { field, operator, operand: rest.slice(operator.length) };
}

function buildFilter(parsed: ParsedCondition, kind: FilterKind, distinctValues: Map<string, string[]>): ColumnFilterModel | null {
    const { field, operator, operand } = parsed;

    if (kind === 'set')
        return buildSetFilter(operator, operand, field, distinctValues);

    if (operand === NULL_TOKEN) {
        const type = operator === '!=' ? 'notBlank' : 'blank';
        return isConditionTypeAllowed(kind, type) ? simple(kind, type) : null;
    }

    if (operator === '=' && splitTop(operand, RANGE_SEPARATOR).length === 2) {
        if (!isConditionTypeAllowed(kind, 'inRange'))
            return null;

        const [from, to] = splitTop(operand, RANGE_SEPARATOR).map(unquote);
        return withRange(kind, from, to);
    }

    const type = decodeOperator(operator);
    if (!type || !isConditionTypeAllowed(kind, type))
        return null;

    return withValue(kind, type, unquote(operand));
}

function buildSetFilter(operator: string, operand: string, field: string, distinctValues: Map<string, string[]>): SetFilterModel | null {
    if (operator !== '=' && operator !== '!=')
        return null;

    // Nothing selected hides every row, which is not the same as having no filter at all.
    if (operator === '=' && operand === '')
        return { filterType: 'set', values: [] };

    const parts = splitTop(operand, VALUE_SEPARATOR);
    const nullListed = parts.some(part => part.trim() === NULL_TOKEN);
    const listed = parts.filter(part => part.trim() !== NULL_TOKEN).map(unquote);

    if (operator === '!=') {
        const known = distinctValues.get(field);
        if (!known || known.length === 0)
            return null;

        const excluded = new Set(listed);
        const remaining = known.filter(value => !excluded.has(value));

        // Blanks stay selected unless they are among the exclusions.
        return { filterType: 'set', values: nullListed ? remaining : [null, ...remaining] };
    }

    return { filterType: 'set', values: nullListed ? [null, ...listed] : listed };
}

function decodeOperator(operator: string): string | null {
    switch (operator) {
        case '=': return 'equals';
        case '!=': return 'notEqual';
        case '~': return 'contains';
        case '>': return 'greaterThan';
        case '>=': return 'greaterThanOrEqual';
        case '<': return 'lessThan';
        case '<=': return 'lessThanOrEqual';
        default: return null;
    }
}

function simple(kind: FilterKind, type: string): ColumnFilterModel {
    if (kind === 'date')
        return { filterType: 'date', type, dateFrom: null, dateTo: null };
    if (kind === 'number')
        return { filterType: 'number', type, filter: null, filterTo: null };

    return { filterType: 'text', type, filter: null };
}

function withValue(kind: FilterKind, type: string, value: string): ColumnFilterModel | null {
    if (kind === 'date') {
        const dateFrom = toDateTime(value);
        return dateFrom === null ? null : { filterType: 'date', type, dateFrom, dateTo: null };
    }

    if (kind === 'number') {
        const num = Number(value);
        return isNaN(num) ? null : { filterType: 'number', type, filter: num, filterTo: null };
    }

    return { filterType: 'text', type, filter: value };
}

function withRange(kind: FilterKind, from: string, to: string): ColumnFilterModel | null {
    if (kind === 'date') {
        const dateFrom = toDateTime(from);
        const dateTo = toDateTime(to);
        return dateFrom === null || dateTo === null
            ? null
            : { filterType: 'date', type: 'inRange', dateFrom, dateTo };
    }

    if (kind === 'number') {
        const fromNum = Number(from);
        const toNum = Number(to);
        return isNaN(fromNum) || isNaN(toNum)
            ? null
            : { filterType: 'number', type: 'inRange', filter: fromNum, filterTo: toNum };
    }

    return null;
}

// AG Grid keeps a time alongside the date it was given. A link is not required to carry a sensible
// date, and AG Grid throws on one it cannot parse, so anything that is not a real calendar day is
// refused here and the filter is dropped rather than applied. `2024-13-45` has the right shape but
// is not a day, so the parts are checked against the date they produce.
function toDateTime(value: string): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value))
        return null;

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    const isRealDay = date.getUTCFullYear() === year
        && date.getUTCMonth() === month - 1
        && date.getUTCDate() === day;

    return isRealDay ? `${value} 00:00:00` : null;
}

/** Splits on a separator that appears outside quotes. */
function splitTop(value: string, separator: string): string[] {
    const parts: string[] = [];
    let current = '';
    let quoted = false;

    for (let i = 0; i < value.length; i++) {
        const char = value[i];

        if (char === '\'') {
            // A doubled quote is a quote within the value.
            if (quoted && value[i + 1] === '\'') {
                current += '\'\'';
                i++;
                continue;
            }
            quoted = !quoted;
            current += char;
            continue;
        }

        if (!quoted && value.startsWith(separator, i)) {
            parts.push(current);
            current = '';
            i += separator.length - 1;
            continue;
        }

        current += char;
    }

    parts.push(current);

    return parts;
}

function unquote(value: string): string {
    const trimmed = value.trim();
    if (trimmed.length >= 2 && trimmed.startsWith('\'') && trimmed.endsWith('\''))
        return trimmed.slice(1, -1).replace(/''/g, '\'');

    return trimmed;
}

// ---------------------------------------------------------------------------- grouping and sorting

export type GridSort = { colId: string; sort: 'asc' | 'desc' };

export function encodeRowGroups(colIds: string[]): string | null {
    return colIds.length > 0 ? colIds.join(',') : null;
}

export function decodeRowGroups(encoded?: string | null): string[] {
    if (!encoded)
        return [];

    return encoded.split(',').map(colId => colId.trim()).filter(colId => !!colId);
}

export function encodeSort(sorts: GridSort[]): string | null {
    return sorts.length > 0 ? sorts.map(({ colId, sort }) => `${colId}:${sort}`).join(',') : null;
}

export function decodeSort(encoded?: string | null): GridSort[] {
    if (!encoded)
        return [];

    const sorts: GridSort[] = [];
    for (const part of encoded.split(',')) {
        const [colId, direction] = part.split(':');
        if (!colId)
            continue;

        sorts.push({ colId: colId.trim(), sort: direction?.trim() === 'desc' ? 'desc' : 'asc' });
    }

    return sorts;
}
